import { loadScript } from 'dashboard/helper/DOMHelpers';

const FACEBOOK_SDK_SRC = 'https://connect.facebook.net/en_US/sdk.js';
const FACEBOOK_SDK_SCRIPT_ID = 'facebook-jssdk';
const FACEBOOK_SDK_CONFIG_KEY = '__chatwootMetaSdkConfig';
const FACEBOOK_OAUTH_FALLBACK_PATH = '/';

const removeFacebookSdkScripts = () => {
  const scriptSelectors = [
    `script#${FACEBOOK_SDK_SCRIPT_ID}`,
    `script[src="${FACEBOOK_SDK_SRC}"]`,
  ];

  document.querySelectorAll(scriptSelectors.join(',')).forEach(script => {
    script.remove();
  });
};

export const resetFacebookSdk = () => {
  removeFacebookSdkScripts();
  delete window.FB;
  delete window.fbAsyncInit;
  delete window.fbSDKLoaded;
  delete window[FACEBOOK_SDK_CONFIG_KEY];
};

const currentFacebookSdkConfig = () => {
  return window[FACEBOOK_SDK_CONFIG_KEY] || {};
};

const setCurrentFacebookSdkConfig = config => {
  window[FACEBOOK_SDK_CONFIG_KEY] = config;
};

const createStableOAuthFallbackController = () => {
  const currentPath = `${window.location.pathname}${window.location.search}${window.location.hash}`;

  if (currentPath === FACEBOOK_OAUTH_FALLBACK_PATH) {
    return () => {};
  }

  window.history.replaceState(
    window.history.state,
    document.title,
    FACEBOOK_OAUTH_FALLBACK_PATH
  );

  let restored = false;

  return () => {
    if (restored) {
      return;
    }

    restored = true;

    window.setTimeout(() => {
      window.history.replaceState(
        window.history.state,
        document.title,
        currentPath
      );
    }, 0);
  };
};

const launchWithStableOAuthFallback = callback => {
  const restorePath = createStableOAuthFallbackController();
  let timeoutId;

  const finalize = () => {
    if (timeoutId) {
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }

    restorePath();
  };

  timeoutId = window.setTimeout(finalize, 120000);

  try {
    callback(finalize);
  } catch (error) {
    finalize();
    throw error;
  }
};

export const loadFacebookSdk = async () => {
  return loadScript(FACEBOOK_SDK_SRC, {
    async: true,
    defer: true,
    crossOrigin: 'anonymous',
    id: FACEBOOK_SDK_SCRIPT_ID,
  });
};

export const initializeFacebook = (appId, apiVersion) => {
  const version = apiVersion || 'v26.0';
  return new Promise(resolve => {
    const init = () => {
      window.FB.init({
        appId,
        autoLogAppEvents: true,
        xfbml: true,
        version,
      });
      setCurrentFacebookSdkConfig({ appId, version });
      resolve();
    };

    if (window.FB) {
      init();
    } else {
      window.fbAsyncInit = init;
    }
  });
};

export const isValidBusinessData = businessData => {
  return businessData && businessData.business_id && businessData.waba_id;
};

export const createMessageHandler = onEmbeddedSignupData => {
  return event => {
    if (!event.origin.endsWith('facebook.com')) return;

    try {
      let data;
      if (typeof event.data === 'string') {
        data = JSON.parse(event.data);
      } else if (typeof event.data === 'object' && event.data !== null) {
        data = event.data;
      } else {
        return;
      }

      if (data.type === 'WA_EMBEDDED_SIGNUP') {
        onEmbeddedSignupData(data);
      }
    } catch {
      // Ignore non-JSON or irrelevant messages
    }
  };
};

export const initWhatsAppEmbeddedSignup = (
  configId,
  { useBusinessAppOnboarding = false } = {}
) => {
  const extras = {
    setup: {},
  };
  if (useBusinessAppOnboarding) {
    extras.featureType = 'whatsapp_business_app_onboarding';
    extras.sessionInfoVersion = '3';
  }

  return new Promise((resolve, reject) => {
    launchWithStableOAuthFallback(finalize => {
      window.FB.login(
        response => {
          finalize();

          if (response.authResponse && response.authResponse.code) {
            resolve(response.authResponse.code);
          } else if (response.error) {
            reject(new Error(response.error));
          } else {
            reject(new Error('Login cancelled'));
          }
        },
        {
          config_id: configId,
          response_type: 'code',
          override_default_response_type: true,
          extras,
        }
      );
    });
  });
};

const shouldForceReloadSdk = (appId, version, forceReload) => {
  if (forceReload) {
    return true;
  }

  if (!window.FB) {
    return false;
  }

  const currentConfig = currentFacebookSdkConfig();
  if (!currentConfig.appId || !currentConfig.version) {
    return true;
  }

  return currentConfig.appId !== appId || currentConfig.version !== version;
};

export const setupFacebookSdk = async (
  appId,
  apiVersion,
  { forceReload = false } = {}
) => {
  const version = apiVersion || 'v26.0';

  if (shouldForceReloadSdk(appId, version, forceReload)) {
    resetFacebookSdk();
  }

  await loadFacebookSdk();
  await initializeFacebook(appId, version);
};
